import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { InformationCircle } from 'assets/icons';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import JustificatifConfirmation, {
  CONFIRMATION_TITLE,
} from 'components/justificatifConfirmation/JustificatifConfirmation';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import useBaseline from 'hooks/useBaseline';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import Tooltip from 'components/tooltips/Tooltips';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

const ModalHolidayHoursCompany = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const [percentage, setPercentage] = useState('');
  const [holidays, setHolidays] = useState([]);
  // Empty until the company says otherwise: pre-selecting the Whit Monday
  // made an unanswered question look like a decision, and the day chosen here
  // is excluded from the increase.
  const [solidarityDay, setSolidarityDay] = useState('');
  const [holidaysError, setHolidaysError] = useState(null);
  const [solidarityError, setSolidarityError] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const justificatif = useJustificatifConfirmation();
  const baseline = useBaseline({ percentage: '', solidarityDay: '' });
  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    if (!modalData?.idCompany) return;
    ApiDatabase.getHolidayHoursCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        // The modal opens with the current percentage and solidarity day
        // pre-filled: it serves for the initial entry as well as for edits (RG-1).
        const current = data?.holidayHours?.percentage;
        const loadedPercentage =
          current === undefined || current === null ? '' : String(current);
        const loadedSolidarityDay = data?.holidayHours?.solidarityDay ?? '';
        setPercentage(loadedPercentage);
        if (loadedSolidarityDay) setSolidarityDay(loadedSolidarityDay);
        baseline.capture({
          percentage: loadedPercentage,
          solidarityDay: loadedSolidarityDay,
        });
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );

    ApiDatabase.getHolidays(
      { token, idCompany: modalData.idCompany },
      (data) => {
        setHolidays(data.holidays ?? []);
        setHolidaysError(null);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.error(err);
        // An empty dropdown with no explanation reads as "there is nothing to
        // choose", which is not what happened.
        setHolidaysError(
          "La liste des jours fériés n'a pas pu être chargée. Réessayez plus tard."
        );
      }
    );
  }, [modalData?.idCompany]);

  const validate = (value) => {
    const parsed = parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(parsed)) return 'Saisissez un nombre.';
    if (parsed < 0 || parsed > 100)
      return 'La majoration doit être comprise entre 0 et 100.';
    if (Math.round(parsed * 10) / 10 !== parsed)
      return 'La majoration accepte au plus une décimale.';
    return null;
  };

  const handleSubmit = () => {
    const invalid = validate(percentage);
    if (invalid) return setError(invalid);

    // Every company works one. Leaving it unset used to be allowed, which is
    // how companies ended up with holiday hours and no day excluded from them.
    //
    // Required only once the list is actually there: it comes from
    // calendrier.api.gouv.fr, and that being down must not take the majoration
    // down with it — there would be nothing to choose from anyway.
    if (!solidarityDay && holidays.length > 0) {
      return setSolidarityError('Choisissez la journée de solidarité.');
    }
    setSolidarityError(null);

    // These values reach contracts and invoices, so the change carries the
    // client's written request for it before anything is written.
    // Nothing to confirm when nothing moved: the confirmation asks for a
    // justificatif, and there would be no change for it to justify.
    if (!baseline.hasChanged({ percentage, solidarityDay })) return;
    if (!justificatif.isConfirming) return justificatif.setIsConfirming(true);
    justificatif.confirm(save);
  };

  const save = (storedJustificatif) => {
    setIsLoading(true);
    ApiDatabase.postHolidayHoursCompany(
      {
        token,
        justificatif: storedJustificatif,
        idCompany: modalData.idCompany,
        percentage: parseFloat(String(percentage).replace(',', '.')),
        // Stored as absent rather than as an empty string, so a company with
        // no solidarity day reads the same as one that never had the field.
        // Empty only in the degraded case above; stored as absent rather than
        // as an empty string, so it reads like a company that never had one.
        solidarityDay: solidarityDay || null,
      },
      (data) => {
        setIsLoading(false);
        toast.success('La majoration des heures de jours fériés a été enregistrée');
        // The panel patches itself rather than refetching, and only this modal
        // holds the holiday list that names the slug — so the label travels
        // with the change instead of the card fetching it back.
        setModalDataReturn({
          type: 'holiday_hours_updated',
          holidayHours: data.holidayHours,
          solidarityDayLabel:
            holidays.find((holiday) => holiday.slug === solidarityDay)?.label ??
            null,
        });
        toggle();
      },
      (err) => {
        setIsLoading(false);
        if (err.name === 'CanceledError') return;
        // The modal stays open, the input intact (RG-6).
        setError(err?.response?.data?.message ?? "L'enregistrement a échoué");
      }
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {justificatif.isConfirming ? CONFIRMATION_TITLE : 'Heures jours fériés travaillés'}
      </Modalheader>
      <ModalBody>
        {justificatif.isConfirming ? (
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
          />
        ) : (
          <>
        <div className={'flex flex-col gap-3 text-sm'}>
          <div className={'text-gray-500'}>
            Les majorations que vous définissez ici s’appliquent à tous les
            jours fériés travaillés (hors journée de solidarité).
          </div>
          <label className={'flex flex-col gap-1'}>
            Majoration
            <div className={'flex items-center gap-2'}>
              <input
                type={'text'}
                className={`border rounded px-2 py-2 w-full ${error ? 'border-red-500' : ''}`}
                value={percentage}
                onChange={(e) => {
                  setPercentage(e.target.value);
                  setError(null);
                }}
              />
              <span className={'text-gray-500'}>%</span>
            </div>
          </label>
          {error && <div className={'text-red-500 text-xs'}>{error}</div>}
          <label className={'flex flex-col gap-1'}>
            <span className={'flex items-center justify-between'}>
              Journée de solidarité
              {/* The modal shell sits at z-50, the same layer the tooltip
                  defaults to: without a higher one the bubble can land behind
                  the modal depending on portal mount order. */}
              <Tooltip
                text={
                  'La journée de solidarité est travaillée sans majoration : elle finance l’autonomie des personnes âgées et handicapées. Le jour choisi ici est exclu du calcul.'
                }
                placement={'top'}
                zIndex={60}
              >
                <span className={'cursor-help'}>
                  <InformationCircle wh={20} color={'#9CA3AF'} />
                </span>
              </Tooltip>
            </span>
            <select
              className={'border rounded px-2 py-2'}
              value={solidarityDay}
              disabled={holidays.length === 0}
              onChange={(e) => {
                setSolidarityDay(e.target.value);
                setSolidarityError(null);
              }}
            >
              {holidays.length === 0 ? (
                <option value={solidarityDay}>
                  {holidaysError ? 'Indisponible' : 'Chargement…'}
                </option>
              ) : (
                // A placeholder, not an answer: the day is required, so this
                // entry is there to be left behind rather than chosen.
                <option value={''} disabled>
                  Sélectionnez une journée
                </option>
              )}
              {holidays.map((holiday) => (
                <option key={holiday.slug} value={holiday.slug}>
                  {holiday.label}
                </option>
              ))}
            </select>
            {(holidaysError || solidarityError) && (
              <div className={'text-red-500 text-xs'}>
                {holidaysError ?? solidarityError}
              </div>
            )}
          </label>
        </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
                  size={'LG'}
                  color={'SECONDARY'}
                  // Backs out of the confirmation, not the modal: the values
                  // being confirmed are still worth seeing.
                  onClick={
                    justificatif.isConfirming
                      ? () => justificatif.setIsConfirming(false)
                      : toggle
                  }
                >
          Annuler
        </Button>
        <Button
          size={'LG'}
          color={
            isLoading ||
            justificatif.isUploading ||
            (!baseline.hasChanged({ percentage, solidarityDay }) ||
              (justificatif.isConfirming && !justificatif.canConfirm))
              ? 'DISABLED'
              : 'PRIMARY'
          }
          onClick={
            isLoading || justificatif.isUploading ? undefined : handleSubmit
          }
        >
          {isLoading || justificatif.isUploading
            ? 'Confirmation ...'
            : 'Confirmer'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalHolidayHoursCompany;
